import { isRegistrationOpen } from '../storage/settings';

export type LoginMode = 'login' | 'create' | 'lock';

export interface LoginCallbacks {
  hasAccounts(): boolean;
  onLogin(id: string, password: string): Promise<void>;
  onCreate(id: string, password: string, acknowledged: boolean): Promise<void>;
  onGuest(): void;
  onUnlock(password: string): Promise<void>;
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Élément #${id} introuvable`);
  return element as T;
}

/** Écran de connexion / création de compte / verrouillage. */
export class LoginScreen {
  private mode: LoginMode = 'login';
  private readonly overlay = byId<HTMLDivElement>('login-overlay');
  private readonly title = byId<HTMLHeadingElement>('login-title');
  private readonly form = byId<HTMLFormElement>('login-form');
  private readonly idField = byId<HTMLLabelElement>('login-id-field');
  private readonly idInput = byId<HTMLInputElement>('login-id');
  private readonly passwordInput = byId<HTMLInputElement>('login-password');
  private readonly confirmField = byId<HTMLLabelElement>('login-confirm-field');
  private readonly confirmInput = byId<HTMLInputElement>('login-confirm');
  private readonly ackField = byId<HTMLLabelElement>('login-ack-field');
  private readonly ackInput = byId<HTMLInputElement>('login-ack');
  private readonly error = byId<HTMLParagraphElement>('login-error');
  private readonly submit = byId<HTMLButtonElement>('login-submit');
  private readonly switchCreate = byId<HTMLButtonElement>('login-switch-create');
  private readonly switchLogin = byId<HTMLButtonElement>('login-switch-login');
  private readonly guest = byId<HTMLButtonElement>('login-guest');
  private readonly closedNote = byId<HTMLParagraphElement>('login-closed-note');

  constructor(private readonly callbacks: LoginCallbacks) {
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submitForm();
    });
    this.switchCreate.addEventListener('click', () => this.setMode('create'));
    this.switchLogin.addEventListener('click', () => this.setMode('login'));
    this.guest.addEventListener('click', () => this.callbacks.onGuest());
  }

  showLogin(): void {
    this.setMode(this.callbacks.hasAccounts() ? 'login' : 'create');
    this.overlay.hidden = false;
    this.passwordInput.focus();
  }

  showLock(accountId: string): void {
    this.mode = 'lock';
    this.idInput.value = accountId;
    this.passwordInput.value = '';
    this.render();
    this.overlay.hidden = false;
    this.passwordInput.focus();
  }

  hide(): void {
    this.overlay.hidden = true;
    this.showError(null);
    this.passwordInput.value = '';
    this.confirmInput.value = '';
    this.ackInput.checked = false;
  }

  setBusy(busy: boolean): void {
    this.submit.disabled = busy;
    this.guest.disabled = busy;
    this.switchCreate.disabled = busy;
    this.switchLogin.disabled = busy;
  }

  showError(message: string | null): void {
    this.error.textContent = message ?? '';
    this.error.hidden = message === null;
  }

  private setMode(mode: LoginMode): void {
    this.mode = mode;
    this.render();
  }

  private render(): void {
    const canCreate = isRegistrationOpen();
    if (!canCreate && this.mode === 'create') this.mode = 'login';
    const isCreate = this.mode === 'create';
    const isLock = this.mode === 'lock';
    this.title.textContent = isLock
      ? 'Session verrouillée'
      : isCreate
        ? 'Créer un compte'
        : 'Se connecter';
    this.submit.textContent = isLock
      ? 'Déverrouiller'
      : isCreate
        ? 'Créer le compte'
        : 'Se connecter';
    this.idField.hidden = isLock;
    this.idInput.disabled = isLock;
    this.confirmField.hidden = !isCreate;
    this.ackField.hidden = !isCreate;
    this.switchCreate.hidden = isLock || isCreate || !canCreate;
    this.switchLogin.hidden = isLock || !isCreate;
    this.guest.hidden = isLock;
    this.closedNote.hidden = isLock || canCreate;
    this.passwordInput.autocomplete = isCreate ? 'new-password' : 'current-password';
    this.showError(null);
  }

  private async submitForm(): Promise<void> {
    this.showError(null);
    const id = this.idInput.value;
    const password = this.passwordInput.value;

    if (this.mode === 'lock') {
      this.setBusy(true);
      try {
        await this.callbacks.onUnlock(password);
      } finally {
        this.setBusy(false);
      }
      return;
    }

    if (this.mode === 'create') {
      if (password !== this.confirmInput.value) {
        this.showError('Les deux mots de passe ne correspondent pas.');
        return;
      }
    }

    this.setBusy(true);
    try {
      if (this.mode === 'create') {
        await this.callbacks.onCreate(id, password, this.ackInput.checked);
      } else {
        await this.callbacks.onLogin(id, password);
      }
    } finally {
      this.setBusy(false);
    }
  }
}
